import { supabaseClient } from "../auth/supabase.service.js";
import { createJSONEditor } from "vanilla-jsoneditor/standalone.js";
const { autorun, observable } = mobx;
import { holdButton } from "../_resources/ui-library.js";
import { mergeInTarget } from "../_resources/utils.pages.js";

const DELIMITER = "⋮";

export default function PagesConfigView({ dom, message }) {
  const { bad, info, progress, good } = message;
  const root = dom;

  let leftEditor = null;
  let rightEditor = null;
  let state = null; // { config, toolId, pageId }
  let panelState = null;
  let pageUrl;

  const Model = {
    async fetchTools(pageId) {
      const { data, error } = await supabaseClient
        .from("pages")
        .select("toolset")
        .eq("id", pageId)
        .single();
      if (error) {
        throw error;
      }
      return data?.toolset?.map(d => ({ toolId: d.id, tool: d.tool })) || [];
    },

    async fetchToolConfig(toolId, pageId, configType = "preferential") {
      progress("Loading config...");
      const { data, error } = await supabaseClient
        .from("configs")
        .select("config, tool_id, page_id")
        .eq("tool_id", toolId)
        .eq("page_id", pageId)
        .eq("type", configType)
        .limit(1);
      if (error) throw error;
      good("Loaded");
      if (data.length == 0 && configType == "preferential") {
        const { data, error } = await supabaseClient
          .from("configs")
          .select("config, tool_id, page_id")
          .eq("tool_id", toolId)
          .eq("page_id", pageId)
          .eq("type", "essential")
          .limit(1);
        if (error) throw error;
        return (data && data.length) ? data.map(d => ({ toolId: d.tool_id, pageId: d.page_id, config: d.config }))[0] : null;
      }
      return (data && data.length) ? data.map(d => ({ toolId: d.tool_id, pageId: d.page_id, config: d.config }))[0] : null;
    },
    async fetchPageData(pageId) {
      const { data, error } = await supabaseClient
        .from("pages")
        .select("base_url, slug")
        .eq("id", pageId)
      if (error) {
        throw error;
      }
      return data.map(d => ({ pageId, url: d.base_url + (d.slug === "__home__" ? "" : d.slug) }))[0] || {};
    },
    async saveConfig(row, type, note) {
      const cfg = rightEditor.get();
      const { error } = await supabaseClient
        .from("configs")
          .upsert({ 
            page_id: row.pageId,
            tool_id: row.toolId,
            type,
            config: cfg.json || JSON.parse(cfg.text),
            note
          });
      if (error) throw error;
      return;
    }
  };

  const Controller = {
    async loadConfig(pageId, site, slug, hasPageEditAccess) {
      clear();
      state = { pageId, site, slug, hasPageEditAccess };
      panelState = { toolPreview: false };
      renderLayout();
      try {
        pageUrl = await Model.fetchPageData(pageId);
      } catch (err) {
        bad("Error getting page url");
        console.error(err);
      }
      try {
        const tools = await Model.fetchTools(pageId);
        View.renderToolsList(tools);
        if (tools.length) {
          await this.loadToolConfig(tools[0].toolId);
        }
      } catch (err) {
        bad("Error loading tools list");
        console.error(err);
        root.html("");
        root.append("p").text("Error loading tools.");
      }
    },

    async loadToolConfig(toolId) {
      try {
        const data = await Model.fetchToolConfig(toolId, state.pageId);
        state.toolId = toolId;
        delete state.config;
        if (data) {
          state.config = data.config;
          info("Config loaded", 3000);
        }
        View.resetConfigTypeSelect();
        View.clearJsonEditorState();
        View.populateLeftEditor(state.config);
        View.populateRightEditor(state.config);
        View.setActiveToolButton(toolId);
        if (panelState.toolPreview) View.renderConfigIframe(pageUrl.url);
      } catch (err) {
        bad("Error loading config");
        console.error(err);
      }
    },
    async loadToolConfigByType(configType) {
      try {
        const data = await Model.fetchToolConfig(state.toolId, state.pageId, configType);
        delete state.config;
        if (data) {
          state.config = data.config;
          info("Config loaded", 3000);
        }
        View.clearJsonEditorState();
        View.populateLeftEditor(state.config);
        View.populateRightEditor(state.config);
      } catch (err) {
        bad("Error loading config");
        console.error(err);
      }
    },
    async save(type = "preferential") {
      try {
        progress("Saving...");
        const note = `${state.site}/${state.slug}`;
        const saved = await Model.saveConfig(state, type, note);
        if (saved) state = saved;
        good("Saved");
      } catch (err) {
        bad("Error saving config");
        console.error(err);
      }
    }
  };

  const View = {
    container: null,
    toolsBar: null,
    editorsWrap: null,
    configTypeSelect: null,
    configIframe: null,
    diffArea: null,
    jsonLeft: {},
    jsonRight: {},
    jsonChangePaths: { left: {}, right: {} },
    currentDiffIndex: -1,
    enableCompare: true,

    renderLayout() {
      this.container = root.append("div").attr("class", "config-subpanel");
      this.container.html(`
        <div class="nav-container">
        </div>
        <div class="config-editors">
          <div class="editor-wrapper left">
            <div class="editor-label-wrapper">
              <div class="editor-label">Current config</div>
              <select id="config-type-select">
                <option value="essential">essential</option>
                <option value="preferential" selected="selected">preferential</option>
              </select>
            </div>
            <div id="containerLeft" class="editor-host jse-theme-dark">
            </div>        
          </div>
          <div class="config-diff"></div>
          <div class="editor-wrapper right">
            <div class="editor-label-wrapper">
              <div class="editor-label">New config (edit manually)</div>
              <input type="checkbox" id="preview-checkbox"/>
              <label for="preview-checkbox">Tool preview</label>
            </div>
            <div id="containerRight" class="editor-host jse-theme-dark">
            </div>        
          </div>
        </div>
      `);

      this.toolsBar = this.container.select(".nav-container");
      const leftHost = this.container.select("#containerLeft").node();
      const rightHost = this.container.select("#containerRight").node();

      this.editorsWrap = this.container.select(".config-editors");
      
      this.configIframe = this.editorsWrap.append("iframe")
        .attr("id", "config-iframe");
      
      this.configTypeSelect = this.editorsWrap.select("#config-type-select");
      this.configTypeSelect.on("change", (event) => {
        Controller.loadToolConfigByType(event.target.value);
      });

      const previewCheckBox = this.editorsWrap.select("#preview-checkbox");
      const rightEditorLabel = this.editorsWrap.select(".editor-wrapper.right .editor-label");
      const rightEditorWrapper = this.editorsWrap.select(".editor-wrapper.right");
      previewCheckBox.on("change", (event) => {
        panelState.toolPreview = event.target.checked;
        if (event.target.checked) {
          this.renderConfigIframe(pageUrl.url);
          rightEditorLabel.text("New config (edit from preview)");
          rightEditorWrapper.classed("is-preview", true);
          rightEditor.updateProps({ readOnly: true });
        } else {
          this.configIframe.attr("src","");
          rightEditorLabel.text("New config (edit manually)");
          rightEditorWrapper.classed("is-preview", false);
          rightEditor.updateProps({ readOnly: false });
        }
        this.editorsWrap.classed("tool-preview", event.target.checked);
      });

      this.diffArea = this.editorsWrap.select(".config-diff");
      this.diffArea.html(`
        <span class="dots">⋮</span>
        <div class="wrapper">
          <span>Differences</span>
          <span class="compare">
            <input type="checkbox" class="compare-checkbox" id="compare-checkbox" checked></input>
            <label for="compare-checkbox">Compare</label>
          </span>
          <span class="count-differences">diff</span>
          <span class="buttons">
            <span class="button">⯅</span>
            <span class="button">⯆</span>
          </span>
        </div>
      `);
      
      const diffCountSpan = this.diffArea.select("span.count-differences");
      autorun(() => {
        const count = this.diffObjects.length;
        if (!this.enableCompare) {
          diffCountSpan.text("disabled");
        } else {
          const countSpanText = count == 0 ? "No differences" : count == 1 ? "1 difference" : `${count} differences`;
          diffCountSpan.text(countSpanText);
        }
      });

      const compareInput = this.diffArea.select("input.compare-checkbox").node();
      compareInput.addEventListener("change", (e) => {
        if (e.target.checked) {
            this.enableCompare = true;
            this.updateChangePaths.call(this, this.jsonLeft, this.jsonRight);
        } else {
          this.enableCompare = false;
          this.diffObjects = [];
        }
        leftEditor.set({ json: this.jsonLeft });
        rightEditor.set({ json: this.jsonRight });
      });

      const upButton = this.diffArea.select("span.buttons .button:nth-child(1)").node();
      const downButton = this.diffArea.select("span.buttons .button:nth-child(2)").node();

      upButton.addEventListener("click", () => {
        if (!this.enableCompare || this.diffObjects.length == 0) return;
        this.currentDiffIndex--;
        this.currentDiffIndex = Math.max(0, this.currentDiffIndex);
        const diff = this.diffObjects[this.currentDiffIndex];
        const path = diff.path.split(DELIMITER);
        leftEditor.scrollTo(diff.left ? path : path.slice(0, -1));
        rightEditor.scrollTo(diff.right ? path : path.slice(0, -1));
      });

      downButton.addEventListener("click", () => {
        if (!this.enableCompare || this.diffObjects.length == 0) return;
        this.currentDiffIndex++;
        this.currentDiffIndex = Math.min(this.diffObjects.length - 1, this.currentDiffIndex);
        const diff = this.diffObjects[this.currentDiffIndex];
        const path = diff.path.split(DELIMITER);
        leftEditor.scrollTo(diff.left ? path : path.slice(0, -1));
        rightEditor.scrollTo(diff.right ? path : path.slice(0, -1));
      });

      const onRenderMenu = function(items) {
        const index = items.findIndex(i => i.text == "table");
        items.splice(index, 1);
        return items;
      }

      const onClassNameLeft = (path) => {
        if (!this.enableCompare) return;
        const b = path.length > 0 ? "/" + path.join("/") : ""; 
        const changePath = this.jsonChangePaths.left[b];
        if (changePath)
          return `diff-${changePath}`;
      }

      const onClassNameRight = (path) => {
        if (!this.enableCompare) return;
        const b = path.length > 0 ? "/" + path.join("/") : ""; 
        const changePath = this.jsonChangePaths.right[b];
        if (changePath)
          return `diff-${changePath}`;
      }

      const optionsLeft = {
        readOnly: true,
        onRenderMenu,
        onClassName: onClassNameLeft,
      }

      const optionsRight = {
        onRenderMenu,
        onClassName: onClassNameRight,
        onChange: (content, previousContent) => {
          if (!this.enableCompare) return;
          try {
            this.jsonRight = content.json ? content.json : JSON.parse(content.text);
          } catch (e) {
          }
          this.jsonChangePaths = { left: {}, right: {} };
          this.updateChangePaths.call(this, this.jsonLeft, this.jsonRight);
          leftEditor.set({ json: this.jsonLeft });
          this.sendConfigToIframe(this.jsonRight);
        }
      }

      leftEditor = createJSONEditor({
        target: leftHost,
        props: optionsLeft
      });
      rightEditor = createJSONEditor({ 
        target: rightHost,
        props: optionsRight
      });

      this.renderButtons();
    },

    resetConfigTypeSelect() {
      this.configTypeSelect.html(`
        <option value="essential">essential</option>
        <option value="preferential" selected="selected">preferential</option>
      `);
    },

    renderConfigIframe(pageUrl) {
      this.configIframe.attr("src", "");
      try {
        const iframeEl = this.configIframe.node();
        const baseUrl = pageUrl.endsWith('/') ? pageUrl.slice(0, -1) : pageUrl;
        const url = `${baseUrl}/?embedded=true#$chart-type=${state.toolId}&url=v2`;
        this.configIframe.attr("src", url);

        if (this._iframeMessageHandler) {
          window.removeEventListener("message", this._iframeMessageHandler);
          this._iframeMessageHandler = null;
        }

        const targetOrigin = this.getTargetOrigin();

        this._iframeMessageHandler = (event) => {
          if (targetOrigin !== "*" && event.origin !== targetOrigin) return;
          const msg = event.data || {};
          if (!msg || !msg.type) return;

          switch (msg.type) {
            case "request-config":
              iframeEl.contentWindow.postMessage({
                type: "response-config",
                toolId: state && state.toolId,
                pageId: state && state.pageId,
                config: this.jsonRight,
                skipSetTool: msg.skipSetTool
              }, targetOrigin);
              break;

            case "set-right-config":
              const cfg = msg.config || {};
              this.jsonRight = mergeInTarget(JSON.parse(JSON.stringify(this.jsonLeft)), cfg);
              this.jsonChangePaths = { left: {}, right: {} };
              this.updateChangePaths(this.jsonLeft, this.jsonRight);
              if (rightEditor) rightEditor.set({ json: this.jsonRight });
              if (leftEditor) leftEditor.set({ json: this.jsonLeft });
              iframeEl.contentWindow.postMessage({ type: "ack-set-right-config" }, targetOrigin);
              break;

            case "iframe-ready":
              break;

            default:
              break;
          }
        };

        window.addEventListener("message", this._iframeMessageHandler);

      } catch (e) {
        console.error("Error setting up iframe communication", e);
      }
    },

    renderToolsList(tools) {
      this.toolsBar.html("");
      if (!tools.length) {
        this.toolsBar.append("div").attr("class", "no-tools").text("No tools on this page");
        return;
      }
      const list = this.toolsBar.selectAll("a.tool-item")
        .data(tools)
        .join("a")
        .attr("class", "tool-item")
        .text(d => d.tool || d.toolId)
        .on("click", (event, d) => {
          Controller.loadToolConfig(d.toolId);
        });
    },

    renderButtons() {
      const buttonWrap = this.container.append("div").attr("class", "editor-center-col");
      const btns = buttonWrap.append("div").attr("class", "config-buttons");
      const savePreferentialBtn = btns.append("button")
        .attr("class", "button bigger save hold-btn")
        .classed("disabled", !state.hasPageEditAccess)
        .text("Save as preferential config");
      const savePreferentialHold = holdButton(savePreferentialBtn, async () => { await Controller.save(); savePreferentialHold.resetHold(); }); 
      btns.append("button")
        .attr("class", "button bigger refresh")
        .text("Refresh")
        .on("click", () => {
          if (state && state.toolId) Controller.loadToolConfig(state.toolId);
        });
      const kebabMenu = btns.append("div")
        .class("kebabmenu")
        .style("visibility", "hidden")
      btns.append("button")
        .attr("class", "button bigger kebab")
        .text("⋮")
        .on("click", () => {
          const isVisible = kebabMenu.style("visibility") === "visible";
          kebabMenu.style("visibility", isVisible ? "hidden" : "visible");
        });
      const saveEssentialBtn = kebabMenu.append("button")
        .attr("class", "button delete hold-btn")
        .classed("disabled", !state.hasPageEditAccess)
        .text("Save as essential");
      const saveEssentialHold = holdButton(saveEssentialBtn, async () => { await Controller.save("essential"); saveEssentialHold.resetHold(); });      
    },

    clearJsonEditorState() {
      this.jsonLeft = {};
      this.jsonRight = {};
      this.jsonChangePaths = { left: {}, right: {} };
      this.diffObjects = [];
    },

    populateLeftEditor(cfg) {
      try {
        this.jsonLeft = JSON.parse(JSON.stringify(cfg)) || {};
        if (cfg) {
          leftEditor.set({ json: cfg });
        } else {
          leftEditor.collapse([], () => true);
          leftEditor.set({ text: "" });
        }
      } catch (e) {
        console.error("Error setting left editor", e);
      }
    },

    populateRightEditor(cfg) {
      try {
        this.jsonRight = cfg || {};
        if (cfg) {
          rightEditor.set({ json: cfg });
        } else {
          rightEditor.collapse([], () => true);
          rightEditor.set({ text: "" });
        }
      } catch (e) {
        console.error("Error setting left editor", e);
      }
    },

    updateChangePaths(jsonLeft, jsonRight) {
      this.diffObjects = diffObjects(jsonLeft, jsonRight);
      this.diffObjects.forEach(d => {
        const changePath = ["", ...d.path.split(DELIMITER)];
        if(d.left != undefined && d.right != undefined) {
          this.jsonChangePaths.left[changePath.join("/")] = "update";
          this.jsonChangePaths.right[changePath.join("/")] = "update";
        } 
        else if (d.left != undefined) {
          this.jsonChangePaths.left[changePath.join("/")] = "delete";
        }
        else if (d.right != undefined) {
          this.jsonChangePaths.right[changePath.join("/")] = "create";
        }
        do {
          changePath.pop();
          this.jsonChangePaths.left[changePath.join("/")] = "child-update";
          this.jsonChangePaths.right[changePath.join("/")] = "child-update";
        } while(changePath.length);
      })
    },

    setActiveToolButton(toolId) {
      this.toolsBar.selectAll("a.tool-item")
        .classed("active", d => d.toolId === toolId);
    },

    getTargetOrigin() {
      try { 
        return new URL(pageUrl).origin; 
      } catch (e) { return "*"; }
    },

    sendConfigToIframe(config) {
      const targetOrigin = this.getTargetOrigin();
      this.configIframe.node().contentWindow.postMessage({
        type: "response-config",
        toolId: state && state.toolId,
        pageId: state && state.pageId,
        config
      }, targetOrigin);
    }
  };
  
  View.diffObjects = observable([]);

  function clear() {
    root.html("");
    try { if (leftEditor) leftEditor.destroy(); } catch(e) {}
    try { if (rightEditor) rightEditor.destroy(); } catch(e) {}
    leftEditor = null;
    rightEditor = null;
    state = null;
  }

  function diffObjects(a, b, path = "") {
    const diffs = [];
    if (a === b) return diffs;
    const aType = Object.prototype.toString.call(a);
    const bType = Object.prototype.toString.call(b);
    if (aType !== bType) {
      diffs.push({ path, left: a, right: b });
      return diffs;
    }
    if (a && typeof a === "object") {
      const aKeys = Array.isArray(a) ? a.map((_, i) => i) : Object.keys(a || {});
      const bKeys = Array.isArray(b) ? b.map((_, i) => i) : Object.keys(b || {});
      const keys = new Set([...(aKeys || []), ...(bKeys || [])]);
      keys.forEach(k => {
        const subPath = path ? `${path}${DELIMITER}${k}` : `${k}`;
        if (typeof a[k] === "undefined") {
          diffs.push({ path: subPath, left: undefined, right: b[k] });
        } else if (typeof b[k] === "undefined") {
          diffs.push({ path: subPath, left: a[k], right: undefined });
        } else {
          diffs.push(...diffObjects(a[k], b[k], subPath));
        }
      });
      return diffs;
    }
    diffs.push({ path, left: a, right: b });
    return diffs;
  }

  function renderLayout() {
    View.renderLayout();
  }

  return {
    loadConfig: async (pageId, site, slug, hasPageEditAccess) => Controller.loadConfig(pageId, site, slug, hasPageEditAccess),
    loadToolConfig: async (toolId) => Controller.loadToolConfig(toolId)
  };
}
